import { Button } from "@babackapp/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@babackapp/ui/components/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import useTranslation from "@babackapp/translations/use-translation";
import { Check, Info, ListFilter } from "lucide-react";
import { useState } from "react";
import { useAnalyticsFilters } from "../hooks/use-analytics-filters";
import { useAnalyticsFilterDefinitions } from "../hooks/use-analytics-filter-definitions";
import { AnalyticsFilterName } from "../providers/analytics-filters.provider";
import { cn } from "@babackapp/ui/lib/utils";

export function AnalyticsFilter() {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedFilterKey, setSelectedFilterKey] =
    useState<AnalyticsFilterName | null>(null);
  const { shop, filters, availableCountries, includeAllOrders, addFilter, removeFilter, setIncludeAllOrders } =
    useAnalyticsFilters();
  const filtersDefinition = useAnalyticsFilterDefinitions({
    shop,
    availableCountries,
  });

  const selectedFilterDefinition = selectedFilterKey
    ? filtersDefinition.get(selectedFilterKey)
    : null;

  const currentFilter = selectedFilterKey ? filters.get(selectedFilterKey) : null;

  const handleSelectOption = (value: string) => {
    if (!selectedFilterKey) return;

    const isSelected = currentFilter?.value.includes(value);

    if (isSelected) {
      removeFilter({ name: selectedFilterKey, value });
    } else {
      addFilter({
        name: selectedFilterKey,
        value,
        condition: "in",
        isMulti: true,
      });
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setSelectedFilterKey(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9">
          <ListFilter className="mr-2 h-4 w-4" />
          {t("misc.filters.title")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[220px]" align="start">
        {!selectedFilterKey && (
          <Command>
            <CommandInput placeholder={t("misc.filters.search")} />
            <CommandList>
              <CommandEmpty>{t("misc.noResultFound")}</CommandEmpty>
              {(shop.preferences?.stats?.returnRates?.minOrdersToDisplay ?? 1) > 1 && (
                <>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => setIncludeAllOrders(!includeAllOrders)}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            includeAllOrders
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50 [&_svg]:invisible",
                          )}
                        >
                          <Check className="h-4 w-4" />
                        </div>
                        <span>{t("analytics.filter.includeAllOrders")}</span>
                      </div>
                      <span title={t("analytics.filter.includeAllOrdersTooltip")}>
                        <Info className="h-3.5 w-3.5 text-muted-foreground" />
                      </span>
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}
              <CommandGroup>
                {Array.from(filtersDefinition.entries()).map(
                  ([key, filterDefinition]) => {
                    const hasActiveFilters = filters.has(key);
                    return (
                      <CommandItem
                        key={key}
                        onSelect={() => setSelectedFilterKey(key)}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          {filterDefinition.category.Icon && (
                            <filterDefinition.category.Icon className="mr-2 h-4 w-4" />
                          )}
                          <span>{filterDefinition.category.label}</span>
                        </div>
                        {hasActiveFilters && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({filters.get(key)?.value.length})
                          </span>
                        )}
                      </CommandItem>
                    );
                  },
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
        {selectedFilterKey && selectedFilterDefinition && (
          <Command>
            <CommandInput
              placeholder={`${t("misc.filters.search")} ${selectedFilterDefinition.category.label.toLowerCase()}...`}
            />
            <CommandList>
              <CommandEmpty>{t("misc.noResultFound")}</CommandEmpty>
              <CommandGroup heading={selectedFilterDefinition.category.label}>
                {Array.from(selectedFilterDefinition.options.entries()).map(
                  ([optionKey, option]) => {
                    const isSelected = currentFilter?.value.includes(optionKey);
                    return (
                      <CommandItem
                        key={optionKey}
                        onSelect={() => handleSelectOption(optionKey)}
                      >
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            isSelected
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50 [&_svg]:invisible",
                          )}
                        >
                          <Check className="h-4 w-4" />
                        </div>
                        {option.Icon && (
                          <option.Icon
                            className={cn(
                              "mr-2 h-4 w-4",
                              option.color,
                              option.background && "p-0.5 rounded",
                            )}
                          />
                        )}
                        <span>{option.label}</span>
                      </CommandItem>
                    );
                  },
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
